import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import contratoVentaService, { ContratoTipo } from '../../../services/contratoVentaService';

interface ContratoTiposDialogProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}

const ContratoTiposDialog: React.FC<ContratoTiposDialogProps> = ({ open, onClose, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [tipos, setTipos] = useState<ContratoTipo[]>([]);
  const [nombre, setNombre] = useState('');
  const [nombres, setNombres] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const lista = await contratoVentaService.getTipos();
      setTipos(lista);
      setNombres(Object.fromEntries(lista.map((t) => [t.id, t.nombre])));
    } catch {
      enqueueSnackbar('No se pudieron cargar los tipos de contrato', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (open) {
      setNombre('');
      void load();
    }
  }, [open, load]);

  const handleCreate = async () => {
    const limpio = nombre.trim();
    if (!limpio) return;
    try {
      setSaving(true);
      await contratoVentaService.createTipo(limpio);
      setNombre('');
      await load();
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo crear el tipo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (tipo: ContratoTipo) => {
    const limpio = (nombres[tipo.id] ?? '').trim();
    if (!limpio || limpio === tipo.nombre) return;
    try {
      await contratoVentaService.updateTipo(tipo.id, limpio, tipo.activo);
      await load();
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo guardar el tipo', { variant: 'error' });
    }
  };

  const handleActivo = async (tipo: ContratoTipo, activo: boolean) => {
    try {
      await contratoVentaService.updateTipo(tipo.id, tipo.nombre, activo);
      await load();
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo actualizar el tipo', { variant: 'error' });
    }
  };

  const handleDelete = async (tipo: ContratoTipo) => {
    if (!window.confirm(`¿Eliminar el tipo "${tipo.nombre}"?`)) return;
    try {
      await contratoVentaService.deleteTipo(tipo.id);
      await load();
      onChanged();
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'No se pudo eliminar el tipo', { variant: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Tipos de contrato</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Estos tipos se usan para clasificar contratos y filtrar la facturación masiva.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <TextField
            label="Nuevo tipo"
            size="small"
            fullWidth
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleCreate();
              }
            }}
            inputProps={{ maxLength: 80 }}
          />
          <Button variant="contained" onClick={() => void handleCreate()} disabled={saving || !nombre.trim()}>
            Agregar
          </Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell align="center">Activo</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {tipos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>No hay tipos cargados</TableCell>
              </TableRow>
            ) : (
              tipos.map((tipo) => (
                <TableRow key={tipo.id}>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={nombres[tipo.id] ?? tipo.nombre}
                      onChange={(e) => setNombres((prev) => ({ ...prev, [tipo.id]: e.target.value }))}
                      onBlur={() => void handleRename(tipo)}
                      inputProps={{ maxLength: 80 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={tipo.activo}
                      onChange={(e) => void handleActivo(tipo, e.target.checked)}
                      inputProps={{ 'aria-label': `Activo ${tipo.nombre}` }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" aria-label={`Eliminar ${tipo.nombre}`} onClick={() => void handleDelete(tipo)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContratoTiposDialog;
